<script lang="ts" module>
  import type { TaskPriority } from '../../lib/types';

  export interface AddTaskProject {
    id: string;
    title: string;
    category?: string | null;
  }

  export interface NewTaskDraft {
    /** The project picked in the sheet; only set when the sheet was given `projects`. */
    projectId?: string;
    title: string;
    notes: string | null;
    priority: TaskPriority;
    tagNames: string[];
  }
</script>

<script lang="ts">
  /**
   * MobileAddTask — the phone replacement for the inline "Add a task..." input
   * (Project detail, ProjectRail): a full-width "+ Add task" row that opens a
   * "New task" bottom sheet with title, notes, priority and tags.
   *
   * - Opening doesn't focus the title on touch: the sheet slides up without the
   *   keyboard, and tapping the title raises it natively (MobileSheet then keeps
   *   the field above it). A blinking caret in the empty title shows where to tap.
   *   With a fine pointer the title is focused (`data-sheet-autofocus`).
   * - Return in the title adds the task and keeps the sheet open, cleared, for
   *   the next one; the Add button adds and closes.
   * - Closing without adding keeps the draft for next time.
   * - The parent owns the mutation (`onSubmit`), same path as its inline input.
   * - Without `projects` the task belongs to the page's project. With `projects` (the
   *   Timeline Tasks board) a Project picker joins the sheet, and its `projectId` comes
   *   back in the draft; it starts on `defaultProjectId`, the Add button waits for a
   *   choice, and the choice stays for the next task.
   * - `open` is bindable and `trigger={false}` drops the row, so a view can open the
   *   sheet from its own "+" (FabButton).
   */
  import MobileSheet from '../MobileSheet.svelte';
  import TagPicker from '../TagPicker.svelte';
  import Icon from '../Icon.svelte';
  import { categoryIcons } from '../../lib/utils';

  interface Props {
    onSubmit: (draft: NewTaskDraft) => void;
    projects?: AddTaskProject[];
    /** Project the picker starts on (e.g. the one filter that is on). */
    defaultProjectId?: string | null;
    open?: boolean;
    /** Render the "Add task" row that opens the sheet. Default true. */
    trigger?: boolean;
  }

  let {
    onSubmit,
    projects,
    defaultProjectId = null,
    open = $bindable(false),
    trigger = true,
  }: Props = $props();

  let projectChoice = $state<string | null>(null);
  let projectId = $derived.by(() => {
    const id = projectChoice ?? defaultProjectId;
    return id && projects?.some((p) => p.id === id) ? id : '';
  });
  let title = $state('');
  let notes = $state('');
  let priority = $state<TaskPriority>('none');
  let tagNames = $state<string[]>([]);
  let lastAdded = $state<string | null>(null);
  let titleFocused = $state(false);

  let canSubmit = $derived(title.trim().length > 0 && (!projects || projectId !== ''));

  const priorityOptions: { value: TaskPriority; label: string; short: string }[] = [
    { value: 'none', label: 'None', short: 'None' },
    { value: 'low', label: 'Low', short: 'Low' },
    { value: 'medium', label: 'Medium', short: 'Med' },
    { value: 'high', label: 'High', short: 'High' },
  ];

  function projectOptionLabel(project: AddTaskProject) {
    return `${(project.category && categoryIcons[project.category]) || '📋'} ${project.title}`;
  }

  function openSheet() {
    lastAdded = null;
    open = true;
  }

  function closeSheet() {
    lastAdded = null;
    open = false;
  }

  function submit({ keepOpen }: { keepOpen: boolean }) {
    const trimmed = title.trim();
    if (!trimmed || !canSubmit) return;
    onSubmit({
      ...(projects ? { projectId } : {}),
      title: trimmed,
      notes: notes.trim() || null,
      priority,
      tagNames: [...tagNames],
    });
    title = '';
    notes = '';
    priority = 'none';
    tagNames = [];
    // Return keeps focus (and the keyboard) in the title for the next task.
    if (keepOpen) {
      lastAdded = trimmed;
    } else {
      lastAdded = null;
      open = false;
    }
  }
</script>

{#if trigger}
  <button type="button" class="add-task-trigger" onclick={openSheet}>
    <Icon name="plus" size={16} />Add task
  </button>
{/if}

<MobileSheet
  {open}
  onClose={closeSheet}
  direction="bottom"
  detents={['half', 'full']}
  initialDetent="half"
  title="New task"
  --mobile-sheet-z="200"
>
  <form
    class="new-task"
    onsubmit={(e) => {
      e.preventDefault();
      submit({ keepOpen: false });
    }}
  >
    <div class="title-field">
      <!-- Tap hint: a blinking caret while the title is empty and not focused -->
      {#if !title && !titleFocused}
        <span class="title-caret" aria-hidden="true"></span>
      {/if}
      <input
        bind:value={title}
        class="title-input"
        type="text"
        autocomplete="off"
        enterkeyhint="next"
        placeholder="Task title"
        aria-label="Task title"
        data-sheet-autofocus
        onfocus={() => (titleFocused = true)}
        onblur={() => (titleFocused = false)}
        onkeydown={(e) => {
          if (e.key === 'Enter' && !e.isComposing) {
            e.preventDefault();
            submit({ keepOpen: true });
          }
        }}
      />
    </div>
    <textarea bind:value={notes} class="notes-input" rows="2" placeholder="Notes" aria-label="Notes"
    ></textarea>

    <div class="props">
      {#if projects}
        <span class="prop-label"><Icon name="folder" size={13} />Project</span>
        <div class="prop-value">
          <select
            class="project-select"
            aria-label="Project"
            value={projectId}
            onchange={(e) => (projectChoice = e.currentTarget.value)}
          >
            <option value="" disabled>Choose a project</option>
            {#each projects as project (project.id)}
              <option value={project.id}>{projectOptionLabel(project)}</option>
            {/each}
          </select>
        </div>
      {/if}

      <span class="prop-label"><Icon name="priority" size={13} />Priority</span>
      <div class="priority-picker" role="radiogroup" aria-label="Priority">
        {#each priorityOptions as opt (opt.value)}
          <button
            type="button"
            class="priority-option priority-{opt.value}"
            class:active={priority === opt.value}
            role="radio"
            aria-checked={priority === opt.value}
            aria-label={opt.label}
            onclick={() => (priority = opt.value)}
          >
            {#if priority === opt.value && opt.value !== 'none'}
              <span class="priority-dot"></span>
            {/if}
            {opt.short}
          </button>
        {/each}
      </div>

      <span class="prop-label"><Icon name="tag" size={13} />Tags</span>
      <div class="prop-value">
        <TagPicker variant="inline" selected={tagNames} onChange={(names) => (tagNames = names)} />
      </div>
    </div>

    <div class="footer">
      <span class="added" aria-live="polite">
        {#if lastAdded}<Icon name="check" size={13} />Added “{lastAdded}”{/if}
      </span>
      <button type="submit" class="submit-btn" disabled={!canSubmit}>Add task</button>
    </div>
  </form>
</MobileSheet>

<style>
  .add-task-trigger {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    min-height: var(--mobile-tap, 44px);
    margin-bottom: 0.5rem;
    padding: 0 0.875rem;
    font-family: inherit;
    font-size: 0.9375rem;
    font-weight: 500;
    color: #2563eb;
    background: white;
    border: 1px dashed #bfdbfe;
    border-radius: 0.625rem;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .add-task-trigger:active {
    background: #eff6ff;
  }

  .add-task-trigger:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .new-task {
    display: flex;
    flex-direction: column;
    padding: 0.75rem 1rem 0.75rem;
  }

  .title-input,
  .notes-input {
    width: 100%;
    font-family: inherit;
    color: #1f2937;
    background: transparent;
    border: none;
    outline: none;
  }

  .title-field {
    position: relative;
  }

  /* Where the text goes, like the real caret; blinks until the field is tapped. */
  .title-caret {
    position: absolute;
    top: 50%;
    left: 0.25rem;
    width: 2px;
    height: 1.5rem;
    margin-top: -0.75rem;
    border-radius: 1px;
    background: #2563eb;
    pointer-events: none;
    animation: title-caret-blink 1.1s steps(1, end) infinite;
  }

  @keyframes title-caret-blink {
    50% {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .title-caret {
      animation: none;
    }
  }

  .title-input {
    height: 2.75rem;
    padding: 0 0.25rem;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .notes-input {
    padding: 0.25rem;
    font-size: 1rem;
    color: #4b5563;
    line-height: 1.5;
    resize: none;
  }

  .title-input::placeholder,
  .notes-input::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  .props {
    display: grid;
    grid-template-columns: 5.25rem 1fr;
    align-items: stretch;
    margin-top: 0.5rem;
  }

  .prop-label,
  .prop-value,
  .priority-picker {
    display: flex;
    align-items: center;
    min-width: 0;
    min-height: 3.25rem;
    border-top: 1px solid #f3f4f6;
  }

  .prop-label {
    gap: 0.375rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .prop-label :global(.icon) {
    color: #9ca3af;
  }

  .priority-picker {
    gap: 0.375rem;
  }

  .priority-option {
    flex: 1;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    height: var(--mobile-tap, 44px);
    padding: 0 0.25rem;
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 600;
    color: #6b7280;
    background: none;
    border: 1px solid #e5e7eb;
    border-radius: 0.625rem;
    cursor: pointer;
  }

  .priority-dot {
    width: 0.4375rem;
    height: 0.4375rem;
    border-radius: 999px;
    background: currentColor;
  }

  .priority-option.active.priority-none {
    color: #374151;
    background: #f3f4f6;
  }

  .priority-option.active.priority-low {
    color: #1d4ed8;
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  .priority-option.active.priority-medium {
    color: #c2410c;
    background: #fff7ed;
    border-color: #fed7aa;
  }

  .priority-option.active.priority-high {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
  }

  .priority-option:focus-visible,
  .submit-btn:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .prop-value {
    padding: 0.375rem 0;
  }

  /* Tags: bigger chips, remove × with a padded hit area (as in the task sheet) */
  .prop-value :global(.rounded-full.border) {
    min-height: 2rem;
    padding: 0 0.625rem;
    font-size: 0.875rem;
  }

  .prop-value :global(button[aria-label^='Remove tag']) {
    position: relative;
    margin-left: 0.25rem;
    font-size: 1rem;
  }

  .prop-value :global(button[aria-label^='Remove tag'])::after {
    content: '';
    position: absolute;
    inset: -0.875rem -0.75rem;
  }

  .prop-value :global(input) {
    height: var(--mobile-tap, 44px);
  }

  .project-select {
    width: 100%;
    height: var(--mobile-tap, 44px);
    padding: 0 0.5rem;
    font-family: inherit;
    font-size: 1rem;
    color: #1f2937;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.625rem;
  }

  .project-select:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .footer {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #f3f4f6;
  }

  .added {
    flex: 1;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    overflow: hidden;
    font-size: 0.8125rem;
    color: #16a34a;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .submit-btn {
    flex-shrink: 0;
    height: 3rem;
    padding: 0 1.5rem;
    font-family: inherit;
    font-size: 1rem;
    font-weight: 600;
    color: white;
    background: #2563eb;
    border: none;
    border-radius: 0.75rem;
    cursor: pointer;
  }

  .submit-btn:disabled {
    background: #93c5fd;
    cursor: default;
  }
</style>
